import React, { createContext, useState, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import Icon from '@mui/material/Icon';

const NotificationContext = createContext();

const getIconForSeverity = (severity) => {
  let iconName = 'notifications';
  let iconColor = 'inherit';
  switch (severity) {
    case 'error':
      iconName = 'error';
      iconColor = 'error';
      break;
    case 'warning':
      iconName = 'warning';
      iconColor = 'warning';
      break;
    case 'info':
      iconName = 'info';
      iconColor = 'info';
      break;
    case 'success':
      iconName = 'check_circle';
      iconColor = 'success';
      break;
    default:
      iconName = 'notifications';
      iconColor = 'action';
  }
  return <Icon color={iconColor}>{iconName}</Icon>;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0); // State for unread count

  const addNotification = useCallback((notificationData) => {
    const newNotification = {
      id: Date.now() + Math.random(),
      title: notificationData.title || ['Notification', 'Something happened'],
      date: new Date().toLocaleString(),
      severity: notificationData.severity || 'info',
      image: notificationData.icon || getIconForSeverity(notificationData.severity || 'info'),
      details: notificationData.details,
    };

    setNotifications((prevNotifications) => [newNotification, ...prevNotifications].slice(0, 20));
    setUnreadCount((prevCount) => prevCount + 1); // Increment unread count
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0); // Reset unread count when clearing all
  }, []);

  // Function to mark notifications as read (reset unread count)
  const markNotificationsAsRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const value = {
    notifications,
    unreadCount, // Expose unread count
    addNotification,
    clearNotifications,
    markNotificationsAsRead, // Expose the new function
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
